import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";
import { useErrorBoundary } from "react-error-boundary";
import { PraxisConfig } from "@shared/praxis-config";
import { FlowDefinition } from "../flows/types";
import { loadFlow } from "../flows";
import { loadPraxisConfig } from "./load-praxis-config";
import { applyTheme, showPage } from "./apply-theme";

type PraxisContextType = {
  config: PraxisConfig;
  flow: FlowDefinition;
};

const PraxisContext = createContext<PraxisContextType | undefined>(undefined);

export const PraxisProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [praxis, setPraxis] = useState<PraxisContextType>();
  const { showBoundary } = useErrorBoundary();

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const config = await loadPraxisConfig();
      const flow = await loadFlow(config.flow);
      if (!cancelled) {
        applyTheme(config.theme, config.hasFavicon);
        setPraxis({ config, flow });
      }
    };
    load().catch((error) => {
      if (!cancelled) {
        showPage();
        showBoundary(error);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [showBoundary]);

  useEffect(() => {
    if (praxis) {
      showPage();
    }
  }, [praxis]);

  if (!praxis) {
    return null;
  }

  return (
    <PraxisContext.Provider value={praxis}>{children}</PraxisContext.Provider>
  );
};

export const usePraxis = (): PraxisContextType => {
  const context = useContext(PraxisContext);
  if (!context) {
    throw new Error("usePraxis must be used within a PraxisProvider");
  }
  return context;
};
